import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import OptimizedImage from "./OptimizedImage";

export default function GallerySection() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const images = [
    { src: "/images/exterior-01.jpg", alt: "Motor City Dubai Exterior View 1" },
    { src: "/images/exterior-02.jpg", alt: "Motor City Dubai Exterior View 2" },
    { src: "/images/exterior-03.jpg", alt: "Motor City Dubai Exterior View 3" },
    { src: "/images/exterior-04.jpg", alt: "Motor City Dubai Exterior View 4" },
    { src: "/images/exterior-05.jpg", alt: "Motor City Dubai Exterior View 5" },
    { src: "/images/exterior-06.jpg", alt: "Motor City Dubai Exterior View 6" },
    { src: "/images/exterior-07.jpg", alt: "Motor City Dubai Exterior View 7" },
    { src: "/images/exterior-08.jpg", alt: "Motor City Dubai Exterior View 8" },
    { src: "/images/exterior-09.jpg", alt: "Motor City Dubai Exterior View 9" },
    { src: "/images/interior-01.jpg", alt: "Motor City Dubai Interior View 1" },
    { src: "/images/interior-02.jpg", alt: "Motor City Dubai Interior View 2" }
  ];

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? images.length - 1 : selectedImage - 1);
    }
  };

  const goToNext = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === images.length - 1 ? 0 : selectedImage + 1);
    }
  };

  return (
    <section className="py-20 bg-background">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Explore Your Future Home
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Take a visual tour of the stunning architecture and luxurious interiors that await you.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <div
                key={index}
                className="relative aspect-square overflow-hidden rounded-lg cursor-pointer group"
                onClick={() => openLightbox(index)}
              >
                <OptimizedImage
                  src={image.src}
                  alt={image.alt}
                  width={800}
                  height={800}
                  className="transition-transform duration-300 group-hover:scale-110"
                  objectFit="cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-semibold">
                    View Full Size
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>

          <button
            className="absolute left-4 text-white hover:text-gray-300 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
          >
            <ChevronLeft className="w-12 h-12" />
          </button>

          <button
            className="absolute right-4 text-white hover:text-gray-300 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
          >
            <ChevronRight className="w-12 h-12" />
          </button>

          <div
            className="max-w-7xl max-h-[90vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[selectedImage].src}
              alt={images[selectedImage].alt}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
            {selectedImage + 1} / {images.length}
          </div>
        </div>
      )}
    </section>
  );
}
